import { Component, OnDestroy, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ApiService } from './api.service';

@Component({
  selector: 'app-admin-project-document-viewer',
  standalone: true,
  imports: [CommonModule, RouterModule],
  template: `
    <div class="fixed inset-0 z-[80] flex flex-col bg-[#F4F8FB] text-slate-900">
      <header class="min-h-[72px] px-5 py-3 bg-white border-b border-[#DCE8F0] shadow-[0_8px_24px_rgba(15,23,42,0.06)] flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div class="min-w-0">
          <a [routerLink]="backLink()" class="text-xs font-bold text-slate-500 hover:text-[#1A6FA6] cursor-pointer inline-flex items-center gap-1">
            <i class="ti ti-arrow-left"></i> {{ backLabel() }}
          </a>
          <h1 class="text-lg font-semibold text-slate-950 mt-1 truncate">{{ title() }}</h1>
          <p class="text-xs text-slate-500 mt-0.5">Full-page project document preview.</p>
        </div>
        <span class="h-9 px-3 rounded-lg bg-[#EAF5FA] border border-[#D5EBF6] text-[#1A6FA6] text-xs font-bold inline-flex items-center gap-2">
          <i class="ti" [ngClass]="isPdf() ? 'ti-file-type-pdf' : (isImage() ? 'ti-photo' : 'ti-file')"></i>
          {{ isPdf() ? 'PDF' : (isImage() ? 'Image' : 'File') }}
        </span>
      </header>

      @if (isLoading()) {
        <div class="m-5 p-10 rounded-xl bg-white border border-[#DCE8F0] text-center text-slate-500">
          <i class="ti ti-loader animate-spin text-3xl text-[#1A6FA6]"></i>
          <p class="text-sm font-semibold mt-3">Loading document...</p>
        </div>
      } @else if (error()) {
        <div class="m-5 p-8 rounded-xl bg-rose-50 border border-rose-200 text-center text-rose-700 text-sm font-semibold">{{ error() }}</div>
      } @else if (safeUrl() && isImage()) {
        <main class="flex-1 min-h-0 overflow-auto p-2">
          <div class="bg-white border border-[#DCE8F0] rounded-lg shadow-sm p-3 min-h-full flex items-center justify-center">
            <img [src]="safeUrl()!" [alt]="title()" class="max-w-full max-h-[calc(100vh-112px)] object-contain">
          </div>
        </main>
      } @else if (safeUrl() && isPdf()) {
        <main class="flex-1 min-h-0 p-2 flex">
          <iframe [src]="safeUrl()!" title="Project document" class="flex-1 w-full rounded-lg border border-[#DCE8F0] bg-white shadow-[0_12px_32px_rgba(15,23,42,0.08)]"></iframe>
        </main>
      } @else {
        <div class="m-5 p-10 rounded-xl bg-white border border-[#DCE8F0] text-center text-slate-600">
          <i class="ti ti-file text-4xl text-slate-400"></i>
          <p class="text-sm font-semibold mt-3">Preview is not available for this file type.</p>
          <button type="button" (click)="downloadFile()" class="mt-4 h-9 px-4 rounded-lg bg-[#1A6FA6] text-white text-xs font-bold cursor-pointer">Download File</button>
        </div>
      }
    </div>
  `
})
export class AdminProjectDocumentViewerComponent implements OnDestroy {
  private route = inject(ActivatedRoute);
  private http = inject(HttpClient);
  private api = inject(ApiService);
  private sanitizer = inject(DomSanitizer);

  isLoading = signal(true);
  error = signal('');
  title = signal('Project Document');
  blobUrl = signal('');
  safeUrl = signal<SafeResourceUrl | null>(null);
  mimeType = signal('');
  backLink = signal<any[] | string>('/admin/projects');
  backLabel = signal('Back to Projects');

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id') || '';
    const projectId = this.route.snapshot.queryParamMap.get('projectId') || '';
    if (projectId) {
      this.backLink.set(['/admin/projects', projectId]);
      this.backLabel.set('Back to Project');
    }
    if (!id) {
      this.isLoading.set(false);
      this.error.set('Document ID is missing.');
      return;
    }

    this.http.get(`${this.api.baseUrl}/projects/document_view.php?id=${encodeURIComponent(id)}`, {
      headers: this.requestHeaders(),
      observe: 'response',
      responseType: 'blob'
    }).subscribe({
      next: (response) => this.handleDocument(response),
      error: () => {
        this.isLoading.set(false);
        this.error.set('Could not load project document.');
      }
    });
  }

  ngOnDestroy(): void {
    const url = this.blobUrl();
    if (url) {
      URL.revokeObjectURL(url);
    }
  }

  downloadFile(): void {
    const url = this.blobUrl();
    if (!url) return;
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = this.title() || 'project-document';
    anchor.click();
  }

  isPdf(): boolean {
    return this.mimeType().includes('pdf');
  }

  isImage(): boolean {
    return this.mimeType().startsWith('image/');
  }

  private handleDocument(response: HttpResponse<Blob>): void {
    this.isLoading.set(false);
    const blob = response.body;
    if (!blob) {
      this.error.set('Document response was empty.');
      return;
    }

    const encodedTitle = response.headers.get('X-Project-Document-Name');
    if (encodedTitle) {
      try {
        this.title.set(decodeURIComponent(encodedTitle));
      } catch {
        this.title.set(encodedTitle);
      }
    }

    this.mimeType.set(blob.type || response.headers.get('Content-Type') || 'application/octet-stream');
    const url = URL.createObjectURL(blob);
    this.blobUrl.set(url);
    this.safeUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(url));
  }

  private requestHeaders(): HttpHeaders {
    const token = this.api.accessToken();
    return new HttpHeaders({
      'Authorization': token ? `Bearer ${token}` : ''
    });
  }
}
